import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Wallet, Eye, Plus, Trash2, Edit2, Tag, Mail, Bell } from 'lucide-react';
import { SUPPORTED_CHAINS } from './walletService';
import CopyButton from './CopyButton';
import { getWatchedItems, addWatchedAddress, removeWatchedAddress, removeWatchedCoin, updateDisplayName, MAX_DISPLAY_NAME_LENGTH, MAX_WATCHED_ITEMS, validateWatchAddress, getNotificationPreferences, updateNotificationPreferences } from './userService';
import { getAllAliases, setAddressAlias, deleteAlias } from './aliasService';
import { buildAddressUrl } from './urlUtils';
import { formatRelativeTime } from './utils/timeUtils';
import { formatAddress } from './addressUtils';
import { validateAlias, ALIAS_MAX_LENGTH } from './utils/aliasValidation';
import SingleLineInput from './components/SingleLineInput';
import { getLabel } from './labelService';

const ProfileModal = ({ isOpen, onClose, user, userProfile, setUser }) => {
  const [watchedItems, setWatchedItems] = useState([]);
  const [watchError, setWatchError] = useState('');
  const [watchSuccess] = useState('');

  // Tab management
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'watched', 'aliases'

  // Alias management
  const [aliases, setAliases] = useState({});
  const [aliasNewAddress, setAliasNewAddress] = useState('');
  const [aliasNewName, setAliasNewName] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [aliasSuccess, setAliasSuccess] = useState('');
  const [editingAlias, setEditingAlias] = useState(null); // {address, currentName}

  // Display name editing
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [displayNameSaving, setDisplayNameSaving] = useState(false);

  // Address labels (from database) - for addresses without aliases
  const [addressLabels, setAddressLabels] = useState({}); // { address: { label, entity, ens, ... } }

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({ inbox: true, email: false });
  const [notifPrefsSaving, setNotifPrefsSaving] = useState(false);
  const [notifPrefsError, setNotifPrefsError] = useState('');

  // Sync editDisplayName with user prop
  useEffect(() => {
    setEditDisplayName(user?.displayName || '');
  }, [user?.displayName]);

  // Detect if user is email-based (no wallet address)
  const isEmailUser = user?.authMethod === 'email';

  // Load watched items and notification preferences
  useEffect(() => {
    if (!isOpen || !user) return;

    const loadWatchedItems = async () => {
      try {
        const items = await getWatchedItems();
        setWatchedItems(items);
      } catch (error) {
        console.error('Error loading watched items:', error);
        setWatchedItems([]);
      }
    };

    const loadNotificationPreferences = async () => {
      try {
        const prefs = await getNotificationPreferences();
        setNotifPrefs(prefs);
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      }
    };

    loadWatchedItems();
    loadNotificationPreferences();
  }, [isOpen, user]);

  // Derived: get just the addresses from watched items (for backward compatibility)
  const watchedAddresses = watchedItems
    .filter(item => item.type === 'address' || !item.type)
    .map(item => item.targetAddress)
    .filter(Boolean);

  // Load aliases
  useEffect(() => {
    if (!isOpen || !user) return;

    const loadAliases = async () => {
      try {
        const loadedAliases = await getAllAliases(user.uid);
        setAliases(loadedAliases);
      } catch (error) {
        console.error('Error loading aliases:', error);
        setAliases({});
      }
    };

    loadAliases();
  }, [isOpen, user]);

  // Load labels for watched addresses that don't have aliases
  useEffect(() => {
    if (!isOpen || watchedAddresses.length === 0) return;

    const loadLabels = async () => {
      const labelsToFetch = watchedAddresses.filter(addr => {
        if (!addr) return false;
        const normalized = addr.toLowerCase();
        // Skip if already has an alias or already has a label
        return !aliases[normalized] && !addressLabels[normalized];
      });

      if (labelsToFetch.length === 0) return;

      // Fetch labels in parallel
      const labelPromises = labelsToFetch.map(async (addr) => {
        try {
          const labelData = await getLabel(addr, 'ethereum');
          return { address: addr.toLowerCase(), data: labelData };
        } catch (error) {
          console.error(`Error fetching label for ${addr}:`, error);
          return { address: addr.toLowerCase(), data: null };
        }
      });

      const results = await Promise.all(labelPromises);
      const newLabels = {};
      results.forEach(({ address, data }) => {
        if (data) {
          newLabels[address] = data;
        }
      });

      setAddressLabels(prev => ({ ...prev, ...newLabels }));
    };

    loadLabels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, watchedAddresses, aliases]); // Intentionally exclude addressLabels to avoid re-fetch loop

  if (!isOpen) return null;

  // Get chain name
  const getChainName = (chainId) => {
    return SUPPORTED_CHAINS[chainId]?.name || `Chain ${chainId}`;
  };

  // Get display name for an address: alias > label > entity > ens
  const getAddressDisplayName = (address) => {
    if (!address) return null;
    const normalized = address.toLowerCase();

    // Priority 1: User's private alias
    if (aliases[normalized]) {
      return { name: aliases[normalized], source: 'Alias' };
    }

    // Priority 2-4: Label data from database
    const labelData = addressLabels[normalized];
    if (labelData) {
      if (labelData.label) {
        return { name: labelData.label, source: 'Label' };
      }
      if (labelData.entity) {
        return { name: labelData.entity, source: 'Entity' };
      }
      if (labelData.ens) {
        return { name: labelData.ens, source: 'ENS' };
      }
      if (labelData.erc20TokenName) {
        const name = labelData.erc20TokenSymbol ? `${labelData.erc20TokenName} (${labelData.erc20TokenSymbol})`: labelData.erc20TokenName;
        return { name, source: 'ERC20-Token'}
      }
    }

    return null;
  };

  // Helper function to generate address markers
  // context: 'watched' | 'aliases' to avoid showing redundant info
  const getAddressMarkers = (address, context = null) => {
    if (!address || !user) return null;

    const normalizedAddress = address.toLowerCase();
    const normalizedWatchedAddresses = watchedAddresses.map(addr => addr?.toLowerCase()).filter(Boolean);

    // Check if it's in watched addresses
    // Don't show in watched addresses tab (redundant)
    if (context !== 'watched' && normalizedWatchedAddresses.includes(normalizedAddress)) {
      return (
        <span className="ml-1 text-xs text-gray-500">
          (Watched)
        </span>
      );
    }

    return null;
  };

  // Handle removing watched address
  const handleRemoveWatchedAddress = async (addressToRemove) => {
    if (!user) {
      setWatchError('You must be signed in to remove watched addresses');
      return;
    }

    try {
      await removeWatchedAddress(addressToRemove);
      // Reload watched items
      const items = await getWatchedItems();
      setWatchedItems(items);
    } catch (error) {
      console.error('Error removing watched address:', error);
      setWatchError('Failed to remove address. Please try again.');
      setTimeout(() => setWatchError(''), 5000);
    }
  };

  // Handle removing watched coin
  const handleRemoveWatchedCoin = async (coinId) => {
    if (!user) {
      setWatchError('You must be signed in to remove watched coins');
      return;
    }

    try {
      await removeWatchedCoin(coinId);
      // Reload watched items
      const items = await getWatchedItems();
      setWatchedItems(items);
    } catch (error) {
      console.error('Error removing watched coin:', error);
      setWatchError('Failed to remove coin. Please try again.');
      setTimeout(() => setWatchError(''), 5000);
    }
  };

  // Handle adding/updating alias
  const handleSaveAlias = async () => {
    setAliasError('');
    setAliasSuccess('');

    // Check if user is signed in (email users have uid but no address)
    if (!user || (!user.address && !user.uid)) {
      setAliasError('You must be signed in to manage aliases');
      return;
    }

    const address = editingAlias ? editingAlias.address : aliasNewAddress.trim();
    const aliasName = editingAlias ? editingAlias.currentName : aliasNewName.trim();

    if (!address) {
      setAliasError('Please enter an address');
      return;
    }

    if (!aliasName) {
      setAliasError('Please enter an alias name');
      return;
    }

    // Validate alias using shared validation
    const validation = validateAlias(aliasName);
    if (!validation.valid) {
      setAliasError(validation.error);
      return;
    }

    // Basic Ethereum address validation
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      setAliasError('Invalid Ethereum address format');
      return;
    }

    try {
      // TODO: Add chain selection UI - currently defaults to ethereum
      await setAddressAlias(user.address, address, aliasName, 'ethereum');
      setAliasSuccess(editingAlias ? 'Alias updated!' : 'Alias added!');

      // Reload aliases
      const loadedAliases = await getAllAliases(user.address);
      setAliases(loadedAliases);

      // Clear form
      setAliasNewAddress('');
      setAliasNewName('');
      setEditingAlias(null);

      // Clear success message after 3 seconds
      setTimeout(() => setAliasSuccess(''), 3000);
    } catch (error) {
      setAliasError(error.message);
    }
  };

  // Handle deleting alias
  const handleDeleteAlias = async (address) => {
    // Check if user is signed in (email users have uid but no address)
    if (!user || (!user.address && !user.uid)) {
      setAliasError('You must be signed in to delete aliases');
      return;
    }

    try {
      // TODO: Determine correct chain - currently defaults to ethereum
      await deleteAlias(user.address, address, 'ethereum');

      // Reload aliases
      const loadedAliases = await getAllAliases(user.address);
      setAliases(loadedAliases);

      setAliasSuccess('Alias deleted!');
      setTimeout(() => setAliasSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting alias:', error);
      setAliasError('Failed to delete alias. Please try again.');
    }
  };

  // Handle editing alias
  const handleEditAlias = (address, currentName) => {
    setEditingAlias({ address, currentName });
    setAliasError('');
    setAliasSuccess('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingAlias(null);
    setAliasError('');
  };

  // Handle saving display name
  const handleSaveDisplayName = async () => {
    setDisplayNameError('');
    setDisplayNameSaving(true);

    try {
      const result = await updateDisplayName(editDisplayName);
      if (result.success) {
        // Update user state and localStorage
        const updatedUser = { ...user, displayName: result.displayName };
        setUser(updatedUser);
        localStorage.setItem('coincommons_user', JSON.stringify(updatedUser));
        setIsEditingDisplayName(false);
      } else {
        setDisplayNameError(result.error);
      }
    } catch (error) {
      console.error('Error saving display name:', error);
      setDisplayNameError('Failed to save display name');
    } finally {
      setDisplayNameSaving(false);
    }
  };

  // Cancel display name editing
  const handleCancelDisplayNameEdit = () => {
    setEditDisplayName(user?.displayName || '');
    setDisplayNameError('');
    setIsEditingDisplayName(false);
  };

  // Handle notification preference toggle
  const handleNotifPrefChange = async (type) => {
    setNotifPrefsError('');
    setNotifPrefsSaving(true);

    const newPrefs = { ...notifPrefs, [type]: !notifPrefs[type] };

    // Validate at least one is enabled
    if (!newPrefs.inbox && !newPrefs.email) {
      setNotifPrefsError('At least one notification method must be enabled');
      setNotifPrefsSaving(false);
      setTimeout(() => setNotifPrefsError(''), 3000);
      return;
    }

    try {
      const result = await updateNotificationPreferences(newPrefs);
      if (result.success) {
        setNotifPrefs(newPrefs);
      } else {
        setNotifPrefsError(result.error || 'Failed to update preferences');
        setTimeout(() => setNotifPrefsError(''), 3000);
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setNotifPrefsError('Failed to update preferences');
      setTimeout(() => setNotifPrefsError(''), 3000);
    } finally {
      setNotifPrefsSaving(false);
    }
  };

  // Handle adding alias to watch list
  const handleWatchAlias = async (address) => {
    setAliasError('');
    setAliasSuccess('');

    // Check if user is signed in (email users have uid but no address)
    if (!user || (!user.address && !user.uid)) {
      setAliasError('You must be signed in to watch addresses');
      setTimeout(() => setAliasError(''), 3000);
      return;
    }

    // Local validation before calling cloud function
    const validation = validateWatchAddress(address, watchedItems);
    if (!validation.valid) {
      setAliasError(validation.error);
      setTimeout(() => setAliasError(''), 3000);
      return;
    }

    try {
      const result = await addWatchedAddress(address);

      if (result.success) {
        setAliasSuccess('Address added to watch list!');
        // Reload watched items
        const items = await getWatchedItems();
        setWatchedItems(items);
        setTimeout(() => setAliasSuccess(''), 3000);
      } else {
        setAliasError(result.error);
        setTimeout(() => setAliasError(''), 3000);
      }
    } catch (error) {
      console.error('Error watching alias:', error);
      setAliasError('Failed to add address to watch list. Please try again.');
      setTimeout(() => setAliasError(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-100 to-gray-200">
      {/* Header Bar */}
      <div className="bg-white bg-opacity-80 backdrop-blur-sm border-b border-gray-300 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-200 rounded-lg transition text-gray-700 font-medium"
          >
            <X className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-300">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide" style={{ scrollBehavior: 'smooth' }}>
            <div className="flex min-w-max">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-3 sm:px-6 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('aliases')}
                className={`px-3 sm:px-6 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === 'aliases'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Address Aliases</span>
                  <span className="sm:hidden">Aliases</span>
                  {Object.keys(aliases).length > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs flex-shrink-0">
                      {Object.keys(aliases).length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('watched')}
                className={`px-3 sm:px-6 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === 'watched'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 flex-shrink-0" />
                  <span>Watching</span>
                  {watchedItems.length > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs flex-shrink-0">
                      {watchedItems.length}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Auth Method Icon - different for email vs wallet users */}
                <div className="flex flex-col items-center mb-6">
                  {isEmailUser ? (
                    <>
                      <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mb-3">
                        <Mail className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-sm text-gray-500">Email Authentication</div>
                    </>
                  ) : (
                    <>
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-3">
                        <Wallet className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-sm text-gray-500">Web3 Wallet Authentication</div>
                    </>
                  )}
                </div>

                {/* Email User - Email Address */}
                {isEmailUser && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4" />
                      Email Address
                    </label>
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800">{user?.email}</span>
                        {user?.email && <CopyButton text={user.email} label="email" />}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">Your email address is your identity, invisible to public</div>
                  </div>
                )}

                {/* Email User - Display Name */}
                {isEmailUser && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Display Name
                    </label>
                    {isEditingDisplayName ? (
                      <SingleLineInput
                        value={editDisplayName}
                        onChange={setEditDisplayName}
                        onSave={handleSaveDisplayName}
                        onCancel={handleCancelDisplayNameEdit}
                        maxLength={MAX_DISPLAY_NAME_LENGTH}
                        error={displayNameError}
                        placeholder="Enter display name..."
                        saving={displayNameSaving}
                      />
                    ) : (
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg">
                        <span className="text-gray-600 truncate min-w-0 mr-2">{user?.displayName || 'Not set'}</span>
                        <button
                          onClick={() => setIsEditingDisplayName(true)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition flex-shrink-0"
                          title="Edit display name"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-500">Your public display name</div>
                  </div>
                )}

                {/* Email User - Username (permanent) */}
                {isEmailUser && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Username
                    </label>
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg">
                      <span className="text-gray-800">@{user?.username || 'Not assigned'}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">Your permanent username (cannot be changed)</div>
                  </div>
                )}

                {/* Wallet User - Wallet Address */}
                {!isEmailUser && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Wallet className="w-4 h-4" />
                      Wallet Address
                    </label>
                    {user?.compositeId && aliases[user.compositeId] ? (
                      <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-600">Private Alias:</span>
                          <span className="text-gray-800 font-semibold">
                            {aliases[user.compositeId]}
                            <span className="ml-2 text-xs text-gray-500">(You)</span>
                          </span>
                          <button
                            onClick={() => {
                              handleEditAlias(user.address, aliases[user.compositeId]);
                              setActiveTab('aliases');
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition"
                            title="Edit your private alias"
                          >
                            <Edit2 className="w-3 h-3" />
                            Edit
                          </button>
                        </div>
                      </div>
                    ) : (
                      user?.address && (
                        <div className="mb-2">
                          <button
                            onClick={() => {
                              setAliasNewAddress(user.address);
                              setActiveTab('aliases');
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 rounded-lg transition w-full justify-center"
                            title="Create a private alias for your wallet address"
                          >
                            <Plus className="w-4 h-4" />
                            Add Your Private Alias
                          </button>
                        </div>
                      )
                    )}
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg">
                      <div className="flex items-center gap-1 flex-wrap">
                        {user?.address ? (
                          <>
                            <Link
                              to={buildAddressUrl(user.address, 'ethereum')}
                              onClick={onClose}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-sm break-all"
                              title={`View ${user.address}`}
                            >
                              <span className="sm:hidden">{formatAddress(user.address)}</span>
                              <span className="hidden sm:inline">{user.address}</span>
                            </Link>
                            <CopyButton text={user.address} label="address" />
                          </>
                        ) : (
                          <div className="text-gray-600 font-mono text-sm break-all">
                            Not connected
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">Your wallet address is your identity</div>
                  </div>
                )}

                {/* Wallet User - Blockchain Network */}
                {!isEmailUser && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Connected Network
                    </label>
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                      {user?.chainId ? getChainName(user.chainId) : 'Unknown'}
                    </div>
                  </div>
                )}

                {/* Member Since - Both user types */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Member Since
                  </label>
                  <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                    {formatRelativeTime(userProfile?.registeredAt)}
                  </div>
                </div>

                {/* Last Sign In - Both user types */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Last Sign In
                  </label>
                  <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                    {formatRelativeTime(userProfile?.lastSeenAt)}
                  </div>
                </div>
              </div>
            )}

            {/* Watching Tab */}
            {activeTab === 'watched' && (
              <div className="space-y-6">
                <div className="text-sm text-gray-600 mb-4">
                  <p className="mb-2">Get notifications when someone interacts with your watched addresses or coins (up to {MAX_WATCHED_ITEMS} total):</p>
                  <ul className="list-disc list-inside text-gray-500 space-y-1 ml-2">
                    <li>New comments on the address or coin</li>
                    <li>Replies to comments</li>
                    <li>Mentions of the address in other comments</li>
                  </ul>
                  <p className="text-gray-500 mt-2">Watched addresses are chain-agnostic — you'll receive notifications from all chains.</p>
                </div>

                {/* Notification Delivery Preferences */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Notification Delivery</span>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {/* Inbox Toggle */}
                    {isEmailUser ? (
                      <button
                        onClick={() => handleNotifPrefChange('inbox')}
                        disabled={notifPrefsSaving}
                        className={`flex items-center gap-2 ${notifPrefsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className={`relative w-9 h-5 rounded-full transition-colors ${
                          notifPrefs.inbox ? 'bg-blue-600' : 'bg-gray-300'
                        }`}>
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            notifPrefs.inbox ? 'left-[18px]' : 'left-0.5'
                          }`} />
                        </div>
                        <span className="text-sm text-gray-700">Inbox</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 opacity-60">
                        <div className="relative w-9 h-5 rounded-full bg-blue-500">
                          <span className="absolute top-0.5 left-[18px] h-4 w-4 rounded-full bg-white shadow" />
                        </div>
                        <span className="text-sm text-gray-500">Inbox</span>
                      </div>
                    )}

                    {/* Email Toggle - Only for email users */}
                    {isEmailUser && (
                      <button
                        onClick={() => handleNotifPrefChange('email')}
                        disabled={notifPrefsSaving}
                        className={`flex items-center gap-2 ${notifPrefsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className={`relative w-9 h-5 rounded-full transition-colors ${
                          notifPrefs.email ? 'bg-blue-600' : 'bg-gray-300'
                        }`}>
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            notifPrefs.email ? 'left-[18px]' : 'left-0.5'
                          }`} />
                        </div>
                        <span className="text-sm text-gray-700">Email</span>
                      </button>
                    )}
                  </div>
                  {notifPrefsError && (
                    <div className="mt-2 text-sm text-red-600">{notifPrefsError}</div>
                  )}
                </div>

                {/* How to watch */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <p>To watch an address or coin, visit its page and click the watch button.</p>
                </div>

                {/* Error/Success messages */}
                {watchError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {watchError}
                  </div>
                )}
                {watchSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    {watchSuccess}
                  </div>
                )}

                {/* List of watched items (addresses + coins) */}
                <div className="space-y-2">
                  {watchedItems.length === 0 ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm text-center">
                      No watched items yet. Add up to {MAX_WATCHED_ITEMS} addresses or coins to monitor.
                    </div>
                  ) : (
                    watchedItems.map((item) => {
                      if (!item) return null;

                      // Handle coin items
                      if (item.type === 'coin') {
                        return (
                          <div
                            key={`c_${item.coinId}`}
                            className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {item.coinImage ? (
                                <img
                                  src={item.coinImage}
                                  alt={item.coinSymbol}
                                  className="w-6 h-6 rounded-full flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs text-gray-500">🪙</span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col gap-0.5">
                                  <Link
                                    to={`/coin/${item.coinId}`}
                                    onClick={onClose}
                                    className="font-semibold text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                                  >
                                    {item.coinName}
                                    <span className="ml-1 text-xs text-gray-400 font-normal uppercase">
                                      ({item.coinSymbol})
                                    </span>
                                  </Link>
                                  <span className="text-xs text-gray-500">Coin</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleRemoveWatchedCoin(item.coinId)}
                                className="p-1 transition text-gray-400 hover:text-red-600"
                                title="Remove this coin from watch list"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      }

                      // Handle address items (default)
                      const address = item.targetAddress;
                      if (!address) return null;

                      const displayInfo = getAddressDisplayName(address);

                      return (
                        <div
                          key={address}
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Eye className="w-4 h-4 text-green-600 flex-shrink-0" title="This address is being watched" />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col gap-0.5">
                                <span className="font-semibold text-sm text-gray-800 truncate">
                                  {displayInfo ? (
                                    <>
                                      {displayInfo.name}
                                      <span className="ml-1 text-xs text-gray-400 font-normal">
                                        ({displayInfo.source})
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-gray-400 font-normal italic">
                                      No name available, you can create an alias for it
                                    </span>
                                  )}
                                </span>
                                <code className="text-xs font-mono text-gray-600">
                                  <span className="sm:hidden">{formatAddress(address)}</span>
                                  <span className="hidden sm:inline">{address}</span>
                                </code>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <CopyButton text={address} label="address" />
                            <button
                              onClick={() => handleRemoveWatchedAddress(address)}
                              className="p-1 transition text-gray-400 hover:text-red-600"
                              title="Remove this address from watch list"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  {watchedItems.length}/{MAX_WATCHED_ITEMS} watched items
                </div>
              </div>
            )}

            {/* Address Aliases Tab */}
            {activeTab === 'aliases' && (
              <div className="space-y-6">
                <div className="text-sm text-gray-600 mb-4">
                  Create your private human-readable names for addresses — only you can see them.
                  <span className="text-gray-500"> Aliases are chain-agnostic and apply across all chains.</span>
                </div>

                {/* Add/Edit alias form */}
                <div className="space-y-2 mb-3">
                  {editingAlias ? (
                    // Editing existing alias
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-xs text-blue-700 mb-2">Editing alias for {formatAddress(editingAlias.address)}</div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={editingAlias.currentName}
                          onChange={(e) => setEditingAlias({ ...editingAlias, currentName: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveAlias()}
                          placeholder="Alias name..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                          maxLength={ALIAS_MAX_LENGTH}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveAlias}
                            className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex-1 sm:flex-none px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {editingAlias.currentName.length}/{ALIAS_MAX_LENGTH} characters
                      </div>
                    </div>
                  ) : (
                    // Adding new alias
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={aliasNewAddress}
                          onChange={(e) => setAliasNewAddress(e.target.value)}
                          placeholder="Full hex address..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={aliasNewName}
                          onChange={(e) => setAliasNewName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveAlias()}
                          placeholder="Alias name..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                          maxLength={ALIAS_MAX_LENGTH}
                        />
                        <button
                          onClick={handleSaveAlias}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm w-full sm:w-auto"
                        >
                          <Plus className="w-4 h-4" />
                          Add
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        {aliasNewName.length}/{ALIAS_MAX_LENGTH} characters
                      </div>
                    </div>
                  )}
                </div>

                {/* Error/Success messages */}
                {aliasError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {aliasError}
                  </div>
                )}
                {aliasSuccess && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    {aliasSuccess}
                  </div>
                )}

                {/* List of aliases */}
                <div className="space-y-2">
                  {Object.keys(aliases).length === 0 ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm text-center">
                      No aliases yet. Add names to addresses for easier identification.
                    </div>
                  ) : (
                    Object.entries(aliases).map(([addressKey, alias]) => {
                      // Address key is now just the normalized address (chain-agnostic)
                      // e.g., "0x123..." for EVM or "1A1zP1..." for Bitcoin
                      const plainAddress = addressKey;

                      const isWatched = watchedAddresses.includes(plainAddress.toLowerCase());
                      const canWatch = watchedItems.length < MAX_WATCHED_ITEMS || isWatched;

                      return (
                        <div
                          key={addressKey}
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Tag className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div>
                                <span className="font-medium text-sm text-gray-800 break-words">
                                  {alias}
                                  {getAddressMarkers(plainAddress, 'aliases')}
                                </span>
                              </div>
                              <div className="font-mono text-xs text-gray-500 truncate">
                                <span className="sm:hidden">{formatAddress(plainAddress)}</span>
                                <span className="hidden sm:inline">{plainAddress}</span>
                              </div>
                            </div>
                            <CopyButton text={plainAddress} label="address" />
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => {
                                if (isWatched) {
                                  try {
                                    await handleRemoveWatchedAddress(plainAddress);
                                    setAliasSuccess('Address removed from watch list!');
                                    setTimeout(() => setAliasSuccess(''), 3000);
                                  } catch (error) {
                                    setAliasError('Failed to remove address from watch list');
                                    setTimeout(() => setAliasError(''), 3000);
                                  }
                                } else {
                                  handleWatchAlias(plainAddress);
                                }
                              }}
                              disabled={!isWatched && !canWatch}
                              className={`p-1 transition ${
                                isWatched
                                  ? 'text-green-600 hover:text-red-600 cursor-pointer'
                                  : !canWatch
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-green-600'
                              }`}
                              title={
                                isWatched
                                  ? 'Remove from watch list'
                                  : !canWatch
                                  ? `Watch list is full (${MAX_WATCHED_ITEMS}/${MAX_WATCHED_ITEMS})`
                                  : 'Add this address to your watch list'
                              }
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditAlias(plainAddress, alias)}
                              className="text-gray-400 hover:text-blue-600 transition p-1"
                              title="Edit this alias"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAlias(plainAddress)}
                              className="text-gray-400 hover:text-red-600 transition p-1"
                              title="Delete this alias"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  {Object.keys(aliases).length} alias(es) saved
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
